// Phase 28 (item F) — the single shared task-assignee picker.
//
// Replaces the duplicated <select> that lived in CaseTaskRow (every task-row
// usage — CaseView, CollectiveCaseView, Dashboard) and the task modal in
// App.jsx. Behavioural fix folded in: an EDIT-GRANT HOLDER (admin / RP /
// caseView.tasks.edit) may assign to ANY collaborator — the old code wrongly
// restricted non-admins to assigning only themselves. `canAssignAll` carries
// that decision from the call site (where the per-case grant is resolvable).

// 154B §2 — THE CONTRACT MOVED WITH THE CONTROL. Three of this component's
// props were the DOM's and not the picker's: `onChange` took a change EVENT,
// `inputRef` was a ref to an HTMLSelectElement, and `onBlur` took a focus
// event. A select that is no longer a <select> cannot honour any of them, and
// widening MenuSelect to hand back an event it never had would be inventing a
// shape to preserve a signature. So the four call sites move with it: the
// value arrives as a string, the ref-callback that focused and opened the
// native picker becomes `autoOpen`, and blur is a fact rather than an event.
// 210 §1 — `import React`, the house remedy for react/react-in-jsx-scope
// (SharedUI.tsx says so): the not-loaded arm is a second JSX element here.
import React from "react";
import { pname, SK } from "../utils";
import { choosableCo } from "../lib/collaboratorState";
import { useTranslation } from "../i18n";
import MenuSelect from "./MenuSelect";
import { ListNotLoaded } from "./SharedUI"; // 210 §1
import { useListsNotLoaded } from "../lib/listLoad"; // 210 §1
import type { CSSProperties, ReactNode } from "react";
import type { Collaborator } from "@shared/entities";

type AssigneePickerProps = {
  co?: Collaborator[];
  value?: any;
  /** the chosen collaborator id as a string, "" for unassigned — every caller
   *  already did `e.target.value ? +e.target.value : null` on it */
  onChange?: (v: string) => void;
  currentUser?: any;
  canAssignAll?: boolean;
  /* Brief 86 §2f — the empty option's text, for the one caller that is a
     FILTER rather than an assignment. §2f rules that the Agenda's
     single-collaborator picker uses the app's existing picker and that no
     generic control is invented; this component IS that picker, and the only
     thing that did not fit was one word — "— Unassigned —" is a statement
     about a task, not a filter that is off. THE FOUR RULES: "if a primitive
     doesn't fit a behavior, widen the primitive; never bend the behavior to
     fit." Default unchanged, so no existing call site moves. */
  emptyLabel?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** the row editors' one-click open: focus the trigger and show the list on
   *  mount, because the mount IS the click that entered edit */
  autoOpen?: boolean;
  onBlur?: () => void;
  /** 160 §4 — the stacking context the HOST sits in, forwarded to MenuSelect.
   *  The menu is a PORTAL to document.body, so it stacks against the whole
   *  page and not against its trigger's ancestors: inside a modal at 300 the
   *  idiom's default 201 paints the list UNDER the backdrop. MenuSelect's own
   *  prop comment already stated the contract ("the document modal sits at 300
   *  and passes 301"); this picker simply had no way to speak it. Absent, the
   *  default is unchanged, so no existing call site moves. */
  zIndex?: number;
};

/* 210 §1 — an assignee is picked from the collaborators: while they could not
   load, the picker says so in its place (ListNotLoaded), never « — Unassigned —
   » alone. */
const CO_LIST: readonly string[] = [SK.co];
export default function AssigneePicker({
  co = [],
  value,
  onChange,
  currentUser,
  canAssignAll = false,
  emptyLabel,
  // BRIEF 87 §11(2) — was `"fi"`, and `App.css` has no `.fi` rule at all, so
  // every default call site rendered an UNSTYLED <select>. That is why §2e-6
  // could call assigned-to "a bare box".
  //
  // AUDITED, all five call sites: FOUR pass className="field-input" explicitly
  // (SubtaskChecklist ×2, CaseTaskRow, TaskModal) and exactly ONE relies on the
  // default — AgendaView.tsx:1237, the collaborator filter. So this changes the
  // appearance of precisely one control, and that control is the named glance.
  //
  // Slice 80's lesson twice over: nothing checks that a className RESOLVES.
  // `grep -c` over App.css was the whole measurement, and §12 arm 4 generalises
  // it into a checkpoint.
  className = "field-input",
  style,
  autoOpen,
  onBlur,
  zIndex,
}: AssigneePickerProps) {
  const { t } = useTranslation();
  const unloaded = useListsNotLoaded(CO_LIST);
  // Edit-grant holders see everyone; others see only themselves (so a
  // self-assign is still possible) plus whoever is already assigned.
  //
  // 75 §4 — "may this person be CHOSEN for new work": archived collaborators
  // leave the options (the ONE predicate, src/lib/collaboratorState.ts) —
  // except the CURRENT assignee, kept even when archived, because an
  // unmatched <select> value silently renders the first option and the next
  // save would re-point the task at whoever sorts first.
  const pool = choosableCo(co, value != null && value !== "" ? [value] : null);
  const list = canAssignAll
    ? pool
    : pool.filter((c) => c.id === currentUser || c.id === value);
  /* `emptyLabel` is a ReactNode in the contract and exactly one caller passes
     one — the Agenda filter's "any collaborator" wording, which is a string.
     A menu row is text, so a non-string node would have nowhere to render;
     String() keeps the prop honest instead of silently dropping it. */
  const emptyText =
    emptyLabel == null
      ? t("common.unassigned_dashed", { fallback: "— Unassigned —" })
      : String(emptyLabel);
  if (unloaded.length) return <ListNotLoaded lists={unloaded} variant="inline" />;
  return (
    <MenuSelect
      className={className}
      style={style}
      autoOpen={autoOpen}
      zIndex={zIndex}
      value={value != null ? String(value) : ""}
      onChange={(v) => onChange && onChange(v)}
      onBlur={onBlur}
      options={[
        ["", emptyText] as const,
        ...list.map((c) => [String(c.id), pname(c)] as const),
      ]}
    />
  );
}
